export const CROP_ASPECT_MODES = {
  landscape: 'landscape',
  '4:3': '4:3',
  portrait: 'portrait',
  '3:4': '3:4',
  square: 'square',
  custom: 'custom',
};

export const DEFAULT_CROP_ASPECT_MODE = CROP_ASPECT_MODES.landscape;

export const CROP_ASPECT_RATIOS = {
  [CROP_ASPECT_MODES.landscape]: 16 / 9,
  [CROP_ASPECT_MODES['4:3']]: 4 / 3,
  [CROP_ASPECT_MODES.portrait]: 9 / 16,
  [CROP_ASPECT_MODES['3:4']]: 3 / 4,
  [CROP_ASPECT_MODES.square]: 1,
};

export const CROP_ASPECT_MODE_LABELS = [
  { mode: CROP_ASPECT_MODES.landscape, label: 'Landscape' },
  { mode: CROP_ASPECT_MODES['4:3'], label: '4:3' },
  { mode: CROP_ASPECT_MODES.portrait, label: 'Portrait' },
  { mode: CROP_ASPECT_MODES['3:4'], label: '3:4' },
  { mode: CROP_ASPECT_MODES.square, label: 'Square' },
  { mode: CROP_ASPECT_MODES.custom, label: 'Custom' },
];

const DEFAULT_INSET = 0.2;
const MIN_PIXEL_SIZE = 24;

export const getAspectRatioForMode = mode => CROP_ASPECT_RATIOS[mode] ?? null;

export const isCustomAspectMode = mode => mode === CROP_ASPECT_MODES.custom;

export const boundsToPixelRect = (bounds, map) => {
  const northWest = map.project([bounds.west, bounds.north]);
  const southEast = map.project([bounds.east, bounds.south]);

  const x = Math.min(northWest.x, southEast.x);
  const y = Math.min(northWest.y, southEast.y);
  const width = Math.abs(southEast.x - northWest.x);
  const height = Math.abs(southEast.y - northWest.y);

  return { x, y, width, height };
};

export const pixelRectToBounds = (rect, map) => {
  const northWest = map.unproject([rect.x, rect.y]);
  const southEast = map.unproject([rect.x + rect.width, rect.y + rect.height]);

  return {
    west: Math.min(northWest.lng, southEast.lng),
    east: Math.max(northWest.lng, southEast.lng),
    south: Math.min(northWest.lat, southEast.lat),
    north: Math.max(northWest.lat, southEast.lat),
  };
};

/** Free-form crop box inset from the map viewport (used for custom mode). */
export const createInsetBounds = (map, inset = DEFAULT_INSET) => {
  const bounds = map.getBounds();
  const west = bounds.getWest();
  const east = bounds.getEast();
  const south = bounds.getSouth();
  const north = bounds.getNorth();

  const lngInset = (east - west) * inset;
  const latInset = (north - south) * inset;

  return {
    west: west + lngInset,
    south: south + latInset,
    east: east - lngInset,
    north: north - latInset,
  };
};

/** Largest centered rectangle of `aspectRatio` (width/height) inside an inset viewport, in screen pixels. */
export const fitAspectBoundsInViewport = (map, aspectRatio, inset = DEFAULT_INSET) => {
  const container = map.getContainer();
  const viewportWidth = container.clientWidth;
  const viewportHeight = container.clientHeight;

  const availWidth = viewportWidth * (1 - inset * 2);
  const availHeight = viewportHeight * (1 - inset * 2);

  let width = availWidth;
  let height = width / aspectRatio;

  if (height > availHeight) {
    height = availHeight;
    width = height * aspectRatio;
  }

  const x = (viewportWidth - width) / 2;
  const y = (viewportHeight - height) / 2;

  return pixelRectToBounds({ x, y, width, height }, map);
};

export const getMapViewportSize = map => {
  const container = map.getContainer();
  return { width: container.clientWidth, height: container.clientHeight };
};

const clampRectToViewport = (rect, viewportWidth, viewportHeight) => {
  const width = Math.max(MIN_PIXEL_SIZE, Math.min(rect.width, viewportWidth));
  const height = Math.max(MIN_PIXEL_SIZE, Math.min(rect.height, viewportHeight));
  const x = Math.max(0, Math.min(rect.x, viewportWidth - width));
  const y = Math.max(0, Math.min(rect.y, viewportHeight - height));

  return { x, y, width, height };
};

const clampAspectSizeToViewport = (width, height, aspectRatio, maxWidth, maxHeight) => {
  let nextWidth = Math.max(MIN_PIXEL_SIZE, Math.min(width, maxWidth));
  let nextHeight = Math.max(MIN_PIXEL_SIZE, Math.min(height, maxHeight));

  if (nextWidth / nextHeight > aspectRatio) {
    nextWidth = Math.min(nextHeight * aspectRatio, maxWidth);
    nextHeight = nextWidth / aspectRatio;
  } else {
    nextHeight = Math.min(nextWidth / aspectRatio, maxHeight);
    nextWidth = nextHeight * aspectRatio;
  }

  nextWidth = Math.max(MIN_PIXEL_SIZE, Math.min(nextWidth, maxWidth));
  nextHeight = Math.max(MIN_PIXEL_SIZE, Math.min(nextHeight, maxHeight));

  return { width: nextWidth, height: nextHeight };
};

/** Clamp a screen pointer to the map viewport. */
export const clampPointerToViewport = (pointer, map) => {
  const { width, height } = getMapViewportSize(map);

  return {
    x: Math.max(0, Math.min(pointer.x, width)),
    y: Math.max(0, Math.min(pointer.y, height)),
  };
};

/** Move a crop rect within the viewport without resizing. */
export const translateBoundsInViewport = (bounds, deltaX, deltaY, map) => {
  const rect = boundsToPixelRect(bounds, map);
  const { width: viewportWidth, height: viewportHeight } = getMapViewportSize(map);

  const nextRect = clampRectToViewport(
    {
      x: rect.x + deltaX,
      y: rect.y + deltaY,
      width: rect.width,
      height: rect.height,
    },
    viewportWidth,
    viewportHeight,
  );

  return pixelRectToBounds(nextRect, map);
};

/**
 * Free-form resize from a corner handle, clamped to the map viewport.
 * @param {'nw'|'ne'|'se'|'sw'} corner
 */
export const resizeBoundsFromCorner = (bounds, corner, pointer, map) => {
  const rect = boundsToPixelRect(bounds, map);
  const { width: viewportWidth, height: viewportHeight } = getMapViewportSize(map);
  const { x: px, y: py } = clampPointerToViewport(pointer, map);

  let nextRect;

  if (corner === 'se') {
    const east = Math.max(rect.x + MIN_PIXEL_SIZE, Math.min(px, viewportWidth));
    const south = Math.max(rect.y + MIN_PIXEL_SIZE, Math.min(py, viewportHeight));
    nextRect = { x: rect.x, y: rect.y, width: east - rect.x, height: south - rect.y };
  } else if (corner === 'nw') {
    const anchorX = rect.x + rect.width;
    const anchorY = rect.y + rect.height;
    const west = Math.max(0, Math.min(px, anchorX - MIN_PIXEL_SIZE));
    const north = Math.max(0, Math.min(py, anchorY - MIN_PIXEL_SIZE));
    nextRect = { x: west, y: north, width: anchorX - west, height: anchorY - north };
  } else if (corner === 'ne') {
    const anchorX = rect.x;
    const anchorY = rect.y + rect.height;
    const east = Math.max(anchorX + MIN_PIXEL_SIZE, Math.min(px, viewportWidth));
    const north = Math.max(0, Math.min(py, anchorY - MIN_PIXEL_SIZE));
    nextRect = { x: anchorX, y: north, width: east - anchorX, height: anchorY - north };
  } else {
    const anchorX = rect.x + rect.width;
    const anchorY = rect.y;
    const west = Math.max(0, Math.min(px, anchorX - MIN_PIXEL_SIZE));
    const south = Math.max(anchorY + MIN_PIXEL_SIZE, Math.min(py, viewportHeight));
    nextRect = { x: west, y: anchorY, width: anchorX - west, height: south - anchorY };
  }

  return pixelRectToBounds(nextRect, map);
};

/** Free-form resize from a north/south/east/west edge, clamped to the map viewport. */
export const resizeBoundsFromEdge = (bounds, edge, pointer, map) => {
  const rect = boundsToPixelRect(bounds, map);
  const { width: viewportWidth, height: viewportHeight } = getMapViewportSize(map);
  const { x: px, y: py } = clampPointerToViewport(pointer, map);

  if (edge === 'north') {
    const north = Math.max(0, Math.min(py, rect.y + rect.height - MIN_PIXEL_SIZE));
    return pixelRectToBounds(
      { x: rect.x, y: north, width: rect.width, height: rect.y + rect.height - north },
      map,
    );
  }

  if (edge === 'south') {
    const south = Math.max(rect.y + MIN_PIXEL_SIZE, Math.min(py, viewportHeight));
    return pixelRectToBounds(
      { x: rect.x, y: rect.y, width: rect.width, height: south - rect.y },
      map,
    );
  }

  if (edge === 'east') {
    const east = Math.max(rect.x + MIN_PIXEL_SIZE, Math.min(px, viewportWidth));
    return pixelRectToBounds(
      { x: rect.x, y: rect.y, width: east - rect.x, height: rect.height },
      map,
    );
  }

  const west = Math.max(0, Math.min(px, rect.x + rect.width - MIN_PIXEL_SIZE));
  return pixelRectToBounds(
    { x: west, y: rect.y, width: rect.x + rect.width - west, height: rect.height },
    map,
  );
};

const applyAspectToSize = (width, height, aspectRatio) => {
  const absWidth = Math.max(MIN_PIXEL_SIZE, Math.abs(width));
  const absHeight = Math.max(MIN_PIXEL_SIZE, Math.abs(height));

  if (absWidth / absHeight > aspectRatio) {
    return { width: absWidth, height: absWidth / aspectRatio };
  }

  return { width: absHeight * aspectRatio, height: absHeight };
};

/**
 * Resize crop bounds from a corner handle while preserving aspect ratio in screen space.
 * @param {'nw'|'ne'|'se'|'sw'} corner
 */
export const resizeBoundsWithAspect = (bounds, corner, pointer, map, aspectRatio) => {
  const rect = boundsToPixelRect(bounds, map);
  const { width: viewportWidth, height: viewportHeight } = getMapViewportSize(map);
  const clampedPointer = clampPointerToViewport(pointer, map);

  let nextRect;

  if (corner === 'se') {
    const rawWidth = clampedPointer.x - rect.x;
    const rawHeight = clampedPointer.y - rect.y;
    const sized = applyAspectToSize(rawWidth, rawHeight, aspectRatio);
    const { width, height } = clampAspectSizeToViewport(
      sized.width,
      sized.height,
      aspectRatio,
      viewportWidth - rect.x,
      viewportHeight - rect.y,
    );
    nextRect = { x: rect.x, y: rect.y, width, height };
  } else if (corner === 'nw') {
    const anchorX = rect.x + rect.width;
    const anchorY = rect.y + rect.height;
    const rawWidth = anchorX - clampedPointer.x;
    const rawHeight = anchorY - clampedPointer.y;
    const sized = applyAspectToSize(rawWidth, rawHeight, aspectRatio);
    const { width, height } = clampAspectSizeToViewport(
      sized.width,
      sized.height,
      aspectRatio,
      anchorX,
      anchorY,
    );
    nextRect = { x: anchorX - width, y: anchorY - height, width, height };
  } else if (corner === 'ne') {
    const anchorX = rect.x;
    const anchorY = rect.y + rect.height;
    const rawWidth = clampedPointer.x - anchorX;
    const rawHeight = anchorY - clampedPointer.y;
    const sized = applyAspectToSize(rawWidth, rawHeight, aspectRatio);
    const { width, height } = clampAspectSizeToViewport(
      sized.width,
      sized.height,
      aspectRatio,
      viewportWidth - anchorX,
      anchorY,
    );
    nextRect = { x: anchorX, y: anchorY - height, width, height };
  } else {
    const anchorX = rect.x + rect.width;
    const anchorY = rect.y;
    const rawWidth = anchorX - clampedPointer.x;
    const rawHeight = clampedPointer.y - anchorY;
    const sized = applyAspectToSize(rawWidth, rawHeight, aspectRatio);
    const { width, height } = clampAspectSizeToViewport(
      sized.width,
      sized.height,
      aspectRatio,
      anchorX,
      viewportHeight - anchorY,
    );
    nextRect = { x: anchorX - width, y: anchorY, width, height };
  }

  return pixelRectToBounds(
    clampRectToViewport(nextRect, viewportWidth, viewportHeight),
    map,
  );
};

export const createBoundsForAspectMode = (map, mode) => {
  if (isCustomAspectMode(mode)) {
    return createInsetBounds(map);
  }

  const aspectRatio = getAspectRatioForMode(mode);
  return fitAspectBoundsInViewport(map, aspectRatio);
};
